<script setup lang="ts">
import {
  actionCellRendererGenerator,
  selectionCellRendererGenerator,
  selectionHeaderCellRendererGenerator,
} from '@/components/common/element-plus/ElTableV2'
import { useSpareParts } from '@/composables/rc/spare-parts'
import { useRowInlineEdit, type ManagedRow } from '@/composables/table-row-inline-edit'
import type { ISparePart } from '@/types/rc/spare-parts'
import { Icon } from '@iconify/vue'
import { ElButton, ElMessage, TableV2FixedDir } from 'element-plus'
import { computed, ref, unref } from 'vue'
import { withUndoNotification } from '@/components/rc/common/RcWithUndoNotification'
import { sparePartsTableCellRendererGenerator } from '@/components/rc/spare-parts/table-render-helpers'
import { useOIDCStore } from '@/stores/oidc.store'
import { storeToRefs } from 'pinia'
import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'

const oidcStore = useOIDCStore()
const { isRcAppManagerUser } = storeToRefs(oidcStore)

const tableRef = ref()

const tableData = defineModel<ManagedRow<ISparePart>[]>({ required: true })
const selection = computed(() => tableData.value.filter((row) => row.selected))

// Setup listener to run on row updating
const onRowUpdate = async (row: ISparePart) => {
  if (!row.id) return
  // Update the part on the BE
  const data = await updateSparesMutation.mutateAsync({
    id: row.id,
    part_type: row.part_type,
    customer_id: row.customer.id,
    part_sn: row.part_sn ?? undefined,
    part_model: { name: row.part_model, version: row.part_model_version ?? null }!,
    price: row.price ?? undefined,
  })
  const updatedIndex = tableData.value.findIndex((part) => part.id === row.id)
  if (updatedIndex !== -1) {
    tableData.value[updatedIndex] = data
  }
}

// Model can be blanked out when changing type
const onSaveValidation = async (row: ISparePart) => {
  const valid = !!row.part_model
  if (!valid) ElMessage.warning('Please set a model')
  return valid
}

const onUpdateFailedMessageTitle = 'Failed to update spare part'
const { rowBeingEdited, onClickEdit } = useRowInlineEdit({ onRowUpdate, onUpdateFailedMessageTitle, onSaveValidation })

const columns = [
  {
    key: 'selected',
    width: 50,
    cellRenderer: selectionCellRendererGenerator(),
    headerCellRenderer: selectionHeaderCellRendererGenerator(tableData),
  },
  {
    title: 'Type',
    key: 'part_type',
    width: 140,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited, { compact: true }),
  },
  {
    title: 'Model',
    key: 'part_model',
    width: 140,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    title: 'SN',
    key: 'part_sn',
    width: 140,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    title: 'Price',
    key: 'price',
    width: 90,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    title: 'Owner',
    key: 'customer',
    width: 140,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    key: 'edit',
    width: 32,
    fixed: TableV2FixedDir.RIGHT,
    cellRenderer: actionCellRendererGenerator(
      { display: 'material-symbols:edit-rounded', editing: 'material-symbols:save-outline' },
      onClickEdit,
      rowBeingEdited
    ),
    hidden: !isRcAppManagerUser.value,
  },
  {
    key: 'delete',
    width: 32,
    fixed: TableV2FixedDir.RIGHT,
    cellRenderer: actionCellRendererGenerator('mdi:trash-can-outline', onClickDelete),
  },
] as any

const {
  deleteSparesMutation,
  addSparesMutation,
  updateSparesMutation,
  deleteManySparesMutation,
  addManySparesMutation,
} = useSpareParts()

/**
 *  Delete the selected parts in the BE and show a notification with an undo button
 */
const onClickDeleteSelection = async () => {
  const _selection = unref(selection)
  // Try to delete all selected
  deleteManySparesMutation.mutate(
    { parts: _selection },
    {
      onSuccess: (partsDeleted) => {
        tableData.value = tableData.value.filter((part) => !partsDeleted.filter((id) => id).includes(part.id!))
        deleteManyPartsUndo(partsDeleted, _selection)
      },
    }
  )
}

/**
 * Shows a notification with 'Undo' button bound to recover a deleted spare part
 * @param partsDeletedIds the list of ids of parts actually deleted, as returned by deleteManySparesMutation
 * @param partsToRestore the original list of parts that were meant to be deleted
 */
const deleteManyPartsUndo = (partsDeletedIds: number[], partsToRestore: ManagedRow<ISparePart>[]) =>
  withUndoNotification({
    text: `${partsDeletedIds.length} parts deleted successfully`,
    onUndo: async () =>
      addManySparesMutation.mutate(
        { parts: partsToRestore.filter((part) => partsDeletedIds.includes(part.id!)) },
        {
          onSuccess: (partsRestored) => {
            tableData.value = [...tableData.value, ...(partsRestored.filter((part) => part) as ISparePart[])]
            NotifySuccess(`${partsRestored.length} parts restored`)
          },
        }
      ),
  })

/**
 *  Delete the part in the BE and show a notification with an undo button
 */
async function onClickDelete(row: ManagedRow<ISparePart>) {
  if (!row.id) return

  deleteSparesMutation.mutate(
    { id: row.id as number },
    {
      onSuccess: () => {
        tableData.value = tableData.value.filter((part) => part.id !== row.id)
        deletePartUndo(row)
      },
    }
  )
}

/**
 * Shows a notification with 'Undo' button bound to recover a deleted spare part
 * @param row the row just deleted and possibly restored
 */
const deletePartUndo = (row: ManagedRow<ISparePart>) =>
  withUndoNotification({
    text: 'Spare part deleted',
    onUndo: async () =>
      addSparesMutation.mutate(
        {
          site_id: row.site.site_id,
          part_type: row.part_type,
          customer_id: row.customer.id,
          part_sn: row.part_sn!,
          part_model: { name: row.part_model!, version: row.part_model_version ?? null }!,
          price: row.price ?? undefined,
        },
        {
          onError: NotifyErrorWrapper('Failed to restore the spare part'),
          onSuccess: (addedPart) => {
            tableData.value = [...tableData.value, addedPart]
            NotifySuccess('Spare part restored')
          },
        }
      ),
  })
</script>

<template>
  <el-auto-resizer :style="{ height: '600px' }">
    <template #default="{ width, height }">
      <el-row v-if="selection.length" class="w-full flex justify-between items-center">
        <p>{{ selection.length }} items selected</p>
        <el-button class="bg-orange-600" @click="onClickDeleteSelection">
          <Icon icon="mdi:trash-can-outline" height="16" />
          <p class="ml-1">Delete</p>
        </el-button>
      </el-row>
      <el-table-v2 ref="tableRef" :columns="columns" :data="tableData" fixed :width="width" :height="height">
        <template #empty>
          <el-empty description="Added parts will be displayed here" />
        </template>
      </el-table-v2>
    </template>
  </el-auto-resizer>
</template>

<style scoped lang="scss">
:deep() {
  .el-table-v2 {
    // Override global table header background color (originally overriden in custom EP theme extension).
    --el-table-header-bg-color: var(--el-dialog-bg-color);

    &__empty {
      width: 100%;
      height: 100%;

      .el-empty {
        height: 100%;
      }
    }

    // Override ElTableV2 background color (defaults to --el-bg-color).
    &__table {
      background-color: var(--el-dialog-bg-color);
    }
  }

  .el-virtual-scrollbar.el-vl__horizontal {
    display: none; // Hide the horizontal scrollbar
  }

  .part-type-cell {
    display: flex;
    align-items: center;
    gap: var(--spacing-spacing-xs);
  }
}
</style>
